import { z } from "zod"
import { DurationSchema, TimelineSchema, PositionSchema } from "./timing"

export const CaptionTextElementSchema = z.object({
  id: z.string(),
  text: z.string(),
  duration: DurationSchema,
  timeline: TimelineSchema,
  color: z.number().min(0).max(2).default(0),
  deleted: z.boolean().default(false),
})

export const CaptionContentSchema = z.object({
  id: z.string(),
  textElements: z.array(CaptionTextElementSchema),
  duration: DurationSchema,
  timeline: TimelineSchema,
})

export const CaptionSegmentSchema = z.object({
  id: z.string(),
  contentType: z.literal("Caption"),
  duration: DurationSchema,
  timeline: TimelineSchema,
  content: CaptionContentSchema,
  position: PositionSchema.default({ type: "CPct", xPct: 0, yPct: 0.8, wPct: 0.9, hPct: 0.1 }),
})

export const FontConfigSchema = z.object({
  family: z.string().default("Montserrat"),
  size: z.number().min(12).max(100).default(50),
  weight: z.string().default("900"),
  style: z.string().default("normal"),
})

export const ColorConfigSchema = z.object({
  default: z.string().default("#FFFFFF"),
  primary: z.string().default("#04F827"),
  secondary: z.string().default("#FFFD03"),
})

export const StrokeConfigSchema = z.object({
  enabled: z.boolean().default(true),
  color: z.string().default("#000000"),
  width: z.number().min(0).max(20).default(3),
})

export const ShadowConfigSchema = z.object({
  enabled: z.boolean().default(true),
  color: z.string().default("#000000"),
  x: z.number().default(2),
  y: z.number().default(2),
  blur: z.number().default(4),
})

export const BackdropConfigSchema = z.object({
  enabled: z.boolean().default(false),
  color: z.string().default("rgba(0,0,0,0.55)"),
  paddingX: z.number().default(16),
  paddingY: z.number().default(6),
  radius: z.number().default(14),
})

export const CaptionStyleConfigSchema = z.object({
  font: FontConfigSchema.default(FontConfigSchema.parse({})),
  colors: ColorConfigSchema.default(ColorConfigSchema.parse({})),
  stroke: StrokeConfigSchema.default(StrokeConfigSchema.parse({})),
  shadow: ShadowConfigSchema.default(ShadowConfigSchema.parse({})),
  backdrop: BackdropConfigSchema.default(BackdropConfigSchema.parse({})),
  animation: z.string().default("karaoke"),
  textTransform: z.string().default("none"),
  position: z.string().default("bottom"),
  positionOffset: z.number().min(0).max(100).default(10),
  wordsPerCaption: z.number().min(1).max(30).default(5),
  activeWordEnabled: z.boolean().default(true),
  lineHeight: z.number().default(1.2),
  // AI keywords use their own colour so the active-word colour stays separate.
  keywordHighlightEnabled: z.boolean().optional(),
  keywordColor: z.string().optional(),
  aiEmojiEnabled: z.boolean().optional(),
  // Normalized caption centre, used when position is "custom" (dragged on the canvas).
  positionX: z.number().min(0).max(1).optional(),
  positionY: z.number().min(0).max(1).optional(),
  maxLines: z.number().min(1).max(3).nullish(),
  // Listed swear words render as "f***" (lib/profanity); new clips get it on from the backend.
  maskProfanity: z.boolean().optional(),
})

export const CaptionKeywordSchema = z.object({
  text: z.string().default(""),
  startMs: z.number(),
  endMs: z.number(),
})

export const CaptionEmojiMarkSchema = z.object({
  emoji: z.string(),
  startMs: z.number(),
  endMs: z.number(),
})

// AI keywords/emoji are timed so they survive caption re-chunking.
export const AICaptionsSchema = z.object({
  keywords: z.array(CaptionKeywordSchema).default([]),
  emojis: z.array(CaptionEmojiMarkSchema).default([]),
})

export type CaptionTextElement = z.infer<typeof CaptionTextElementSchema>
export type CaptionContent = z.infer<typeof CaptionContentSchema>
export type CaptionSegment = z.infer<typeof CaptionSegmentSchema>
export type FontConfig = z.infer<typeof FontConfigSchema>
export type ColorConfig = z.infer<typeof ColorConfigSchema>
export type StrokeConfig = z.infer<typeof StrokeConfigSchema>
export type ShadowConfig = z.infer<typeof ShadowConfigSchema>
export type BackdropConfig = z.infer<typeof BackdropConfigSchema>
export type CaptionStyleConfig = z.infer<typeof CaptionStyleConfigSchema>
export type CaptionKeyword = z.infer<typeof CaptionKeywordSchema>
export type CaptionEmojiMark = z.infer<typeof CaptionEmojiMarkSchema>
export type AICaptions = z.infer<typeof AICaptionsSchema>
